import { TokenService, makeBackendBlobRequest } from './tokenService';

// Generate attribute list Excel via backend
export const generateAttributeList = async (rulesetKey: string): Promise<void> => {
  try {
    console.log(`📊 Generating attribute list via backend for ruleset: ${rulesetKey}`);
    
    const response = await makeBackendBlobRequest(`/attribute-list?rulesetKey=${rulesetKey}`, {
      method: 'GET',
    });

    // Get the filename from the Content-Disposition header
    const contentDisposition = response.headers.get('Content-Disposition');
    const filenameMatch = contentDisposition?.match(/filename="(.+)"/);
    const filename = filenameMatch ? filenameMatch[1] : 'attribute_list.xlsx';

    // Convert response to blob and download
    const blob = await response.blob();
    const url = window.URL.createObjectURL(blob);
    const link = document.createElement('a');
    link.href = url;
    link.download = filename;
    document.body.appendChild(link);
    link.click();
    document.body.removeChild(link);
    window.URL.revokeObjectURL(url);

    console.log(`✅ Attribute list downloaded: ${filename}`);
  } catch (error) {
    console.error('❌ Error generating attribute list via backend:', error);
    throw error;
  }
};

// Generate audit report Excel via backend with streaming progress
export const generateAuditReportWithProgress = async (
  rulesetKey: string,
  onProgress: (message: string) => void,
  attachments?: File[]
): Promise<void> => {
  // Create AbortController for timeout control
  const controller = new AbortController();
  let timeoutId: NodeJS.Timeout | undefined;
  
  try {
    console.log(`📊 Generating audit report with progress via backend for ruleset: ${rulesetKey}`);
    
    timeoutId = setTimeout(() => controller.abort(), 30 * 60 * 1000); // 15 minutes

    // Convert attachments to base64 if provided
    const attachmentsAsBase64: { data: string; mimeType: string }[] = [];
    if (attachments && attachments.length > 0) {
      for (const file of attachments) {
        const base64Data = await new Promise<string>((resolve, reject) => {
          const reader = new FileReader();
          reader.onload = () => {
            const result = reader.result as string;
            // Remove the data URL prefix (e.g., "data:application/pdf;base64,")
            const base64 = result.split(',')[1];
            resolve(base64);
          };
          reader.onerror = reject;
          reader.readAsDataURL(file);
        });
        
        attachmentsAsBase64.push({
          data: base64Data,
          mimeType: file.type
        });
      }
    }
    console.log(attachmentsAsBase64);
    const requestBody = attachmentsAsBase64.length > 0 ? { attachments: attachmentsAsBase64 } : {};

    const response = await makeBackendBlobRequest(`/audit-report-stream?rulesetKey=${rulesetKey}`, {
      method: 'POST',
      signal: controller.signal,
      body: JSON.stringify(requestBody),
      headers: {
        'Content-Type': 'application/json',
      },
    });

    // Clear timeout if request completes successfully
    if (timeoutId) clearTimeout(timeoutId);

    if (!response.ok) {
      const errorData = await response.json().catch(() => ({}));
      console.log('🔒 Backend server error detected');
      throw new Error(errorData.error || `HTTP error! status: ${response.status}`);
    }

    if (!response.body) {
      throw new Error('No response body for streaming');
    }

    const reader = response.body.getReader();
    const decoder = new TextDecoder();
    let buffer = ''; // Buffer for incomplete SSE messages

    try {
      while (true) {
        const { done, value } = await reader.read();
        
        if (done) {
          // Process any remaining data in buffer
          if (buffer.trim()) {
            const finalLines = [buffer];
            for (const line of finalLines) {
              if (line.startsWith('data: ')) {
                try {
                  const data = JSON.parse(line.slice(6));
                  console.log(`📨 Frontend received final SSE:`, data);
                  
                  if (data.message === 'Complete' && data.data) {
                    // Handle completion with file download (same logic as below)
                    console.log('🎯 Starting file download process...');
                    const { filename, file, contentType } = data.data;
                    console.log('📁 File details:', { filename, contentType, fileSize: file?.length });
                    
                    if (!file) {
                      console.error('❌ No file data received');
                      throw new Error('No file data received from server');
                    }
                    
                    try {
                      // Convert base64 to blob and download
                      console.log('🔄 Converting base64 to blob...');
                      const binaryString = atob(file);
                      console.log('✅ Base64 decoded, binary length:', binaryString.length);
                      
                      const bytes = new Uint8Array(binaryString.length);
                      for (let i = 0; i < binaryString.length; i++) {
                        bytes[i] = binaryString.charCodeAt(i);
                      }
                      const blob = new Blob([bytes], { type: contentType });
                      console.log('✅ Blob created, size:', blob.size);
                      
                      const url = window.URL.createObjectURL(blob);
                      console.log('✅ Object URL created:', url);
                      
                      const link = document.createElement('a');
                      link.href = url;
                      link.download = filename;
                      document.body.appendChild(link);
                      console.log('🖱️ Triggering download...');
                      link.click();
                      document.body.removeChild(link);
                      window.URL.revokeObjectURL(url);
                      console.log('✅ Download triggered successfully');
                    } catch (downloadError) {
                      console.error('❌ Error during download process:', downloadError);
                      throw downloadError;
                    }

                    onProgress('Download completed');
                    console.log(`✅ Streaming audit report downloaded: ${filename}`);
                    return;
                    
                  } else if (data.message === 'Error') {
                    throw new Error(data.data?.error || 'Unknown error occurred');
                  } else {
                    // Regular progress update
                    console.log(`📢 Calling onProgress with:`, data.message);
                    onProgress(data.message);
                  }
                } catch (parseError) {
                  console.warn('Failed to parse final SSE data:', line);
                }
              }
            }
          }
          break;
        }

        const chunk = decoder.decode(value);
        buffer += chunk; // Add to buffer
        const lines = buffer.split('\n');
        
        // Keep the last line in buffer if it doesn't end with \n (incomplete)
        buffer = lines.pop() || '';
        
        for (const line of lines) {
          if (line.startsWith('data: ')) {
            try {
              const data = JSON.parse(line.slice(6));
              console.log(`📨 Frontend received SSE:`, data);
              
              if (data.message === 'Complete' && data.data) {
                // Handle completion with file download
                console.log('🎯 Starting file download process...');
                const { filename, file, contentType } = data.data;
                console.log('📁 File details:', { filename, contentType, fileSize: file?.length });
                
                if (!file) {
                  console.error('❌ No file data received');
                  throw new Error('No file data received from server');
                }
                
                try {
                  // Convert base64 to blob and download
                  console.log('🔄 Converting base64 to blob...');
                  const binaryString = atob(file);
                  console.log('✅ Base64 decoded, binary length:', binaryString.length);
                  
                  const bytes = new Uint8Array(binaryString.length);
                  for (let i = 0; i < binaryString.length; i++) {
                    bytes[i] = binaryString.charCodeAt(i);
                  }
                  const blob = new Blob([bytes], { type: contentType });
                  console.log('✅ Blob created, size:', blob.size);
                  
                  const url = window.URL.createObjectURL(blob);
                  console.log('✅ Object URL created:', url);
                  
                  const link = document.createElement('a');
                  link.href = url;
                  link.download = filename;
                  document.body.appendChild(link);
                  console.log('🖱️ Triggering download...');
                  link.click();
                  document.body.removeChild(link);
                  window.URL.revokeObjectURL(url);
                  console.log('✅ Download triggered successfully');
                } catch (downloadError) {
                  console.error('❌ Error during download process:', downloadError);
                  throw downloadError;
                }

                onProgress('Download completed');
                console.log(`✅ Streaming audit report downloaded: ${filename}`);
                return;
                
              } else if (data.message === 'Error') {
                throw new Error(data.data?.error || 'Unknown error occurred');
              } else {
                // Regular progress update
                console.log(`📢 Calling onProgress with:`, data.message);
                onProgress(data.message);
              }
            } catch (parseError) {
              console.warn('Failed to parse SSE data:', line);
            }
          }
        }
      }
    } finally {
      reader.releaseLock();
      if (timeoutId) clearTimeout(timeoutId);
    }
  } catch (error) {
    if (timeoutId) clearTimeout(timeoutId);
    
    if (error instanceof Error && error.name === 'AbortError') {
      console.error('❌ Audit report generation timed out after 15 minutes');
      throw new Error('Report generation timed out after 15 minutes. Please try with a smaller ruleset or contact support.');
    }
    
    console.error('❌ Error generating streaming audit report:', error);
    
    throw error;
  }
};

// Generate workspace heuristics audit report Excel via backend with streaming progress
export const generateWorkspaceHeuristicsAuditReportWithProgress = async (
  workspaceKey: string,
  onProgress: (message: string) => void
): Promise<void> => {
  // Create AbortController for timeout control
  const controller = new AbortController();
  let timeoutId: NodeJS.Timeout | undefined;
  
  try {
    console.log(`📊 Generating workspace heuristics audit report with progress via backend for workspace: ${workspaceKey}`);
    
    timeoutId = setTimeout(() => controller.abort(), 30 * 60 * 1000); // 30 minutes

    const response = await makeBackendBlobRequest(`/workspace-overwrites-audit-report?workspaceKey=${workspaceKey}`, {
      method: 'GET',
      signal: controller.signal,
    });

    // Clear timeout if request completes successfully
    if (timeoutId) clearTimeout(timeoutId);

    if (!response.ok) {
      const errorData = await response.json().catch(() => ({}));
      console.log('🔒 Backend server error detected, invalidating token for safety');
      TokenService.clearToken();
      throw new Error(errorData.error || `HTTP error! status: ${response.status}`);
    }

    if (!response.body) {
      throw new Error('No response body for streaming');
    }

    const reader = response.body.getReader();
    const decoder = new TextDecoder();
    let buffer = ''; // Buffer for incomplete SSE messages

    try {
      while (true) {
        const { done, value } = await reader.read();
        
        if (done) {
          // Process any remaining data in buffer
          if (buffer.trim()) {
            const finalLines = [buffer];
            for (const line of finalLines) {
              if (line.startsWith('data: ')) {
                try {
                  const data = JSON.parse(line.slice(6));
                  console.log(`📨 Frontend received final SSE:`, data);
                  
                  if (data.message === 'Complete' && data.data) {
                    // Handle completion with file download
                    console.log('🎯 Starting file download process...');
                    const { filename, file, contentType } = data.data;
                    console.log('📁 File details:', { filename, contentType, fileSize: file?.length });
                    
                    if (!file) {
                      console.error('❌ No file data received');
                      throw new Error('No file data received from server');
                    }
                    
                    try {
                      // Convert base64 to blob and download
                      console.log('🔄 Converting base64 to blob...');
                      const binaryString = atob(file);
                      console.log('✅ Base64 decoded, binary length:', binaryString.length);
                      
                      const bytes = new Uint8Array(binaryString.length);
                      for (let i = 0; i < binaryString.length; i++) {
                        bytes[i] = binaryString.charCodeAt(i);
                      }
                      const blob = new Blob([bytes], { type: contentType });
                      console.log('✅ Blob created, size:', blob.size);
                      
                      const url = window.URL.createObjectURL(blob);
                      console.log('✅ Object URL created:', url);
                      
                      const link = document.createElement('a');
                      link.href = url;
                      link.download = filename;
                      document.body.appendChild(link);
                      console.log('🖱️ Triggering download...');
                      link.click();
                      document.body.removeChild(link);
                      window.URL.revokeObjectURL(url);
                      console.log('✅ Download triggered successfully');
                    } catch (downloadError) {
                      console.error('❌ Error during download process:', downloadError);
                      throw downloadError;
                    }

                    onProgress('Download completed');
                    console.log(`✅ Streaming workspace heuristics audit report downloaded: ${filename}`);
                    return;
                    
                  } else if (data.message === 'Error') {
                    throw new Error(data.data?.error || 'Unknown error occurred');
                  } else {
                    // Regular progress update
                    console.log(`📢 Calling onProgress with:`, data.message);
                    onProgress(data.message);
                  }
                } catch (parseError) {
                  console.warn('Failed to parse final SSE data:', line);
                }
              }
            }
          }
          break;
        }

        const chunk = decoder.decode(value);
        buffer += chunk; // Add to buffer
        const lines = buffer.split('\n');
        
        // Keep the last line in buffer if it doesn't end with \n (incomplete)
        buffer = lines.pop() || '';
        
        for (const line of lines) {
          if (line.startsWith('data: ')) {
            try {
              const data = JSON.parse(line.slice(6));
              console.log(`📨 Frontend received SSE:`, data);
              
              if (data.message === 'Complete' && data.data) {
                // Handle completion with file download
                console.log('🎯 Starting file download process...');
                const { filename, file, contentType } = data.data;
                console.log('📁 File details:', { filename, contentType, fileSize: file?.length });
                
                if (!file) {
                  console.error('❌ No file data received');
                  throw new Error('No file data received from server');
                }
                
                try {
                  // Convert base64 to blob and download
                  console.log('🔄 Converting base64 to blob...');
                  const binaryString = atob(file);
                  console.log('✅ Base64 decoded, binary length:', binaryString.length);
                  
                  const bytes = new Uint8Array(binaryString.length);
                  for (let i = 0; i < binaryString.length; i++) {
                    bytes[i] = binaryString.charCodeAt(i);
                  }
                  const blob = new Blob([bytes], { type: contentType });
                  console.log('✅ Blob created, size:', blob.size);
                  
                  const url = window.URL.createObjectURL(blob);
                  console.log('✅ Object URL created:', url);
                  
                  const link = document.createElement('a');
                  link.href = url;
                  link.download = filename;
                  document.body.appendChild(link);
                  console.log('🖱️ Triggering download...');
                  link.click();
                  document.body.removeChild(link);
                  window.URL.revokeObjectURL(url);
                  console.log('✅ Download triggered successfully');
                } catch (downloadError) {
                  console.error('❌ Error during download process:', downloadError);
                  throw downloadError;
                }

                onProgress('Download completed');
                console.log(`✅ Streaming workspace heuristics audit report downloaded: ${filename}`);
                return;
                
              } else if (data.message === 'Error') {
                throw new Error(data.data?.error || 'Unknown error occurred');
              } else {
                // Regular progress update
                console.log(`📢 Calling onProgress with:`, data.message);
                onProgress(data.message);
              }
            } catch (parseError) {
              console.warn('Failed to parse SSE data:', line);
            }
          }
        }
      }
    } finally {
      reader.releaseLock();
      if (timeoutId) clearTimeout(timeoutId);
    }
  } catch (error) {
    if (timeoutId) clearTimeout(timeoutId);
    
    if (error instanceof Error && error.name === 'AbortError') {
      console.error('❌ Workspace heuristics audit report generation timed out after 30 minutes');
      throw new Error('Report generation timed out after 30 minutes. Please try with a smaller workspace or contact support.');
    }
    
    console.error('❌ Error generating streaming workspace heuristics audit report:', error);
    
    throw error;
  }
};
